'use client'

import { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react'
import { supabase } from '@/utils/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface Notification {
  id: string | number;
  user_id: string | null;
  title: string;
  message: string;
  time: string;
  read: boolean;
  date: Date;
  action_url?: string;
  brand_context?: string | null;
  is_global_for_user?: boolean;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'time' | 'read' | 'date' | 'is_global_for_user'>) => void;
  markAsRead: (id: string | number) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}

interface NotificationsProviderProps {
  children: ReactNode;
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const { user, getAllowedBrandIds } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const lastNotificationCheck = useRef<Date>(new Date())
  
  // Store IDs of global notifications read by the current user
  const [readGlobalNotificationIds, setReadGlobalNotificationIds] = useState<Set<string>>(new Set());

  // Get unread count
  const unreadCount = notifications.filter(n => !n.read).length
  
  // Load user-specific read statuses for global notifications
  useEffect(() => {
    if (!user) {
      setReadGlobalNotificationIds(new Set()); // Clear if no user
      return;
    }
    const fetchReadGlobalStatuses = async () => {
      try {
        const { data, error } = await supabase
          .from('user_read_global_notifications')
          .select('notification_id')
          .eq('user_id', user.id);

        if (error) {
          console.error('[NotificationsContext] Error fetching read statuses for global notifications:', error);
          return;
        }
        if (data) {
          setReadGlobalNotificationIds(new Set(data.map(r => r.notification_id)));
          console.log('[NotificationsContext] Loaded read statuses for global notifications:', data.length);
        }
      } catch (e) {
        console.error('[NotificationsContext] Exception fetching read statuses for global notifications:', e);
      }
    };
    fetchReadGlobalStatuses();
  }, [user]);
  
  // Load notifications from local storage on initial load (still useful for persistence)
  useEffect(() => {
    // --- START: Clear local storage at the very beginning of provider initialization ---
    localStorage.removeItem('newsclipping_notifications'); 
    console.log('[NotificationsContext] Cleared notifications from local storage on mount.');
    // --- END: Clear local storage ---

    // Since local storage is cleared, loadedNotifications will be empty,
    // and lastNotificationCheck.current will be initialized to epoch.
    let loadedNotifications: Notification[] = [] 

    // --- Existing Local Storage Loading Logic (will find nothing now, kept for structure if behavior changes later) ---
    const savedNotifications = localStorage.getItem('newsclipping_notifications') // This will be null
    if (savedNotifications) {
      try {
        const parsedNotifications = JSON.parse(savedNotifications)
        // Convert date strings back to Date objects and FILTER out old non-DB notifications
        const notificationsWithDates = parsedNotifications
          .map((n: any): Notification | null => { // Allow returning null for filtering
            // Basic validation: check if essential fields exist
            // Check for id AND title/message. Date check happens later.
            if (!n.id || !n.title || !n.message) { 
              console.warn('Filtering out notification missing essential fields:', n);
              return null;
            }
            
            // Filter out notifications with non-string IDs (likely old timestamps)
            if (typeof n.id !== 'string') {
              console.log('Filtering out old local notification (non-string ID):', n.id);
              return null;
            } 
            
            // Attempt to parse the date
            const parsedDate = new Date(n.date);
            if (isNaN(parsedDate.getTime())) { // Check if date is valid
               console.warn('Filtering out notification with invalid date:', n);
               return null;
            }
            
            // Return the valid notification object
            return {
              ...n,
              id: n.id, // Keep the string ID
              user_id: n.user_id || null, // Ensure user_id is present or null
              read: typeof n.read === 'boolean' ? n.read : false, // Ensure read is boolean
              time: n.time || formatTimeAgo(parsedDate),
              date: parsedDate,
              brand_context: n.brand_context || null
            };
          })
          // Explicitly type 'n' in the filter predicate
          .filter((n: Notification | null): n is Notification => n !== null);
          
        loadedNotifications = notificationsWithDates // Assign here
        setNotifications(loadedNotifications)
        console.log(`Loaded ${loadedNotifications.length} valid notifications from local storage.`);
      } catch (error) {
        console.error('Failed to parse notifications from local storage:', error)
        // Ensure state is empty if parsing fails
        setNotifications([]);
        loadedNotifications = [];
      }
    } else {
       console.log('No notifications found in local storage (expected after clearing).');
    }
    
    // Initialize last check time. Since loadedNotifications will be empty, this will always go to the else branch.
    if (loadedNotifications.length > 0) {
        // This branch should not be hit if localStorage is cleared above
        const latestDate = new Date(Math.max(...loadedNotifications.map(n => n.date.getTime())));
        lastNotificationCheck.current = latestDate;
        console.log(`[NotificationsContext] Initial lastNotificationCheck set from (unexpected) local storage: ${latestDate.toISOString()}`);
    } else {
        // Start from beginning of time so the first DB fetch gets everything
        lastNotificationCheck.current = new Date(0); 
        console.log('[NotificationsContext] Initial lastNotificationCheck set to epoch.');
    }
    // --- End Existing Logic ---

  }, []) // Run only once on mount
  
  // Save notifications to local storage when they change
  useEffect(() => {
    localStorage.setItem('newsclipping_notifications', JSON.stringify(notifications))
  }, [notifications])
  
  // Combine notifications with user-specific read statuses for global ones
  // This effect runs when `notifications` state changes OR `readGlobalNotificationIds` changes
  useEffect(() => {
    setNotifications(prevNotifications =>
      prevNotifications.map(n => {
        let finalReadStatus = n.read; // Default to its inherent read status (from DB is_read or local)
        let isGlobalRelevant = false;

        if (n.user_id === null && typeof n.id === 'string') { // Global DB notification
          isGlobalRelevant = true; // Mark it as potentially global for the user
          finalReadStatus = readGlobalNotificationIds.has(n.id);
        }
        return { ...n, read: finalReadStatus, is_global_for_user: isGlobalRelevant };
      })
    );
  }, [readGlobalNotificationIds, user]); // Rerun when user changes to re-evaluate global read statuses if notifications were loaded before readGlobalNotificationIds

  // Check for new database notifications periodically
  useEffect(() => {
    if (!user) return
    
    const checkForDatabaseNotifications = async () => {
      console.log(`[NotificationsContext] Checking DB for notifications created after: ${lastNotificationCheck.current.toISOString()}`);
      if (!user) {
          console.log('[NotificationsContext] User not available, skipping DB check.');
          return;
      }
      
      try {
        const orFilter = `user_id.eq.${user.id},user_id.is.null`; // Fetch user-specific AND global
        const { data: dbNotifications, error } = await supabase
          .from('notifications')
          .select('*')
          .or(orFilter)
          .gt('created_at', lastNotificationCheck.current.toISOString())
          .order('created_at', { ascending: false })
        
        if (error) {
          console.error('[NotificationsContext] Error fetching notifications:', error)
          return
        }
        
        console.log(`[NotificationsContext] Raw data received from DB:`, dbNotifications);

        if (dbNotifications && dbNotifications.length > 0) {
          const latestTimestamp = new Date(Math.max(...dbNotifications.map(n => new Date(n.created_at).getTime())));
          // Ensure next check starts right after the latest notification fetched to avoid re-fetching the same one
          const nextCheckTimestamp = new Date(latestTimestamp.getTime() + 1); 
          lastNotificationCheck.current = nextCheckTimestamp;
          console.log(`[NotificationsContext] Updated lastNotificationCheck.current to: ${nextCheckTimestamp.toISOString()}`);

          const allowedBrandsResult = getAllowedBrandIds();
          let userAllowedBrandSet: Set<string> | null = null;
          let canSeeAllBrands = false;
          let hasNoBrandAccess = false;

          if (allowedBrandsResult === 'all') {
            canSeeAllBrands = true;
          } else if (allowedBrandsResult === 'none') {
            hasNoBrandAccess = true;
          } else if (Array.isArray(allowedBrandsResult)) {
            userAllowedBrandSet = new Set(allowedBrandsResult);
          }

          const processedNotifications: Notification[] = dbNotifications
            .map(dbNotif => {
              // Determine initial read state:
              // For user-specific, it's dbNotif.is_read.
              // For global, it's initially false, then overridden by readGlobalNotificationIds set.
              let initialReadState = dbNotif.is_read;
              let isGlobalRelevant = false;
              if (dbNotif.user_id === null && typeof dbNotif.id === 'string') {
                isGlobalRelevant = true;
                initialReadState = readGlobalNotificationIds.has(dbNotif.id);
              }

              return {
                id: String(dbNotif.id), // Ensure ID is string
                user_id: dbNotif.user_id,
                title: dbNotif.title,
                message: dbNotif.message,
                time: formatTimeAgo(new Date(dbNotif.created_at)),
                read: initialReadState,
                date: new Date(dbNotif.created_at),
                action_url: dbNotif.action_url,
                brand_context: dbNotif.brand_context || null,
                is_global_for_user: isGlobalRelevant,
              };
            })
            .filter(notif => {
              if (notif.user_id) { 
                return true; 
              }
              if (notif.brand_context) { 
                if (hasNoBrandAccess) return false; 
                if (canSeeAllBrands) return true; 
                if (userAllowedBrandSet && userAllowedBrandSet.has(notif.brand_context)) {
                  return true; 
                }
                return false; 
              }
              // If it's global (user_id:null) but has NO brand_context, show it (legacy global notifications)
              // Or decide to hide them if all global notifications must now have a brand_context
              return true; 
            });
          
          console.log(`[NotificationsContext] Fetched ${dbNotifications.length} raw, processed to ${processedNotifications.length} relevant new notification(s) from DB.`);

          if (processedNotifications.length > 0) {
            setNotifications(prev => {
              const existingIds = new Set(prev.map(n => String(n.id))); // Ensure string IDs for comparison
              const uniqueNewNotifications = processedNotifications.filter(n => !existingIds.has(String(n.id)));
              console.log(`[NotificationsContext] Unique new notifications to add after client-side brand filtering:`, uniqueNewNotifications);

              if (uniqueNewNotifications.length === 0) {
                  console.log('[NotificationsContext] No unique new notifications found after filtering.');
                  return prev; 
              }
              
              const updatedNotifications = [...uniqueNewNotifications, ...prev]
                .sort((a, b) => b.date.getTime() - a.date.getTime())
                .map(n => { // Re-apply global read status after potential additions
                    let finalReadStatus = n.read;
                    if (n.user_id === null && typeof n.id === 'string') {
                         finalReadStatus = readGlobalNotificationIds.has(n.id);
                    }
                    return {...n, read: finalReadStatus };
                });
              console.log(`[NotificationsContext] Final notifications array length after update: ${updatedNotifications.length}`);
              return updatedNotifications;
            })
          } else {
             console.log("[NotificationsContext] No new notifications found in DB for the given time range that are relevant to the user.");
          }
        } else {
           console.log("[NotificationsContext] No new notifications found in DB for the given time range.");
        }
      } catch (error) {
        console.error('[NotificationsContext] Error during DB notification check/processing:', error);
      }
    }
    
    checkForDatabaseNotifications();
    const intervalId = setInterval(checkForDatabaseNotifications, 15000);
    return () => clearInterval(intervalId);
  }, [user, getAllowedBrandIds, readGlobalNotificationIds]); // Added readGlobalNotificationIds dependency
  
  // Add a new notification
  const addNotification = (notification: Omit<Notification, 'id' | 'time' | 'read' | 'date' | 'is_global_for_user'>) => {
    // This function is for client-side generated notifications (e.g., local alerts, not from DB)
    // These typically won't be global or brand-centric in the same way.
    // Their ID will be a timestamp (number).
    const date = new Date();
    const newNotification: Notification = {
      id: Date.now(), // Numeric ID for local-only
      ...notification,
      time: formatTimeAgo(date),
      read: false,
      date,
      is_global_for_user: false, // Local notifications are not global in this context
    };
    setNotifications(prev => [newNotification, ...prev].sort((a, b) => b.date.getTime() - a.date.getTime()));
  };
  
  // Mark a notification as read
  const markAsRead = async (id: string | number) => {
    if (!user) return;
    const stringId = String(id); // Ensure ID is string for DB operations

    const notificationToUpdate = notifications.find(n => String(n.id) === stringId);
    if (!notificationToUpdate) return;

    // Optimistic UI update
    setNotifications(prev => 
      prev.map(n => 
        String(n.id) === stringId 
          ? { ...n, read: true } 
          : n
      )
    );
    
    if (notificationToUpdate.user_id && typeof notificationToUpdate.id === 'string') { // User-specific DB notification
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', stringId) // id is UUID string
          .eq('user_id', user.id);
        
        if (error) {
          console.error('Error marking user-specific notification as read in DB:', error);
          // Revert optimistic update
          setNotifications(prev => prev.map(n => String(n.id) === stringId ? { ...n, read: false } : n));
        }
      } catch (e) { /* ... error handling ... */ }
    } else if (notificationToUpdate.user_id === null && typeof notificationToUpdate.id === 'string') { // Global DB notification
      try {
        // Check if already marked as read to prevent duplicate inserts
        if (readGlobalNotificationIds.has(stringId)) return;

        const { error } = await supabase
          .from('user_read_global_notifications')
          .insert({ user_id: user.id, notification_id: stringId }); // notification_id is UUID string
        
        if (error) {
          console.error('Error marking global notification as read for user:', error);
          // Revert optimistic update
          setNotifications(prev => prev.map(n => String(n.id) === stringId ? { ...n, read: false } : n));
        } else {
          // Add to local set to reflect change immediately and prevent re-fetch dependency loop
          setReadGlobalNotificationIds(prev => new Set(prev).add(stringId));
        }
      } catch (e) { /* ... error handling ... */ }
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;

    const unreadNotificationsInState = notifications.filter(n => !n.read);
    if (unreadNotificationsInState.length === 0) return;

    // Optimistic UI update
    const originalNotifications = notifications.map(n => ({...n})); // Deep copy for potential revert
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );

    const userSpecificToUpdate = unreadNotificationsInState
      .filter(n => n.user_id && typeof n.id === 'string' && !n.read)
      .map(n => String(n.id));

    const globalToUpdate = unreadNotificationsInState
      .filter(n => n.user_id === null && typeof n.id === 'string' && !n.read && !readGlobalNotificationIds.has(String(n.id)))
      .map(n => ({ user_id: user.id, notification_id: String(n.id) })); // Prepare for insert

    let userSpecificError = false;
    let globalError = false;

    if (userSpecificToUpdate.length > 0) {
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ is_read: true })
          .in('id', userSpecificToUpdate)
          .eq('user_id', user.id);
        if (error) {
          userSpecificError = true;
          console.error('Error marking all user-specific notifications as read in DB:', error);
        }
      } catch (e) { userSpecificError = true; /* ... */ }
    }

    if (globalToUpdate.length > 0) {
      try {
        const { error } = await supabase
          .from('user_read_global_notifications')
          .insert(globalToUpdate);
        if (error) {
          globalError = true;
          console.error('Error marking all global notifications as read for user:', error);
        } else {
           setReadGlobalNotificationIds(prev => {
             const newSet = new Set(prev);
             globalToUpdate.forEach(item => newSet.add(item.notification_id));
             return newSet;
           });
        }
      } catch (e) { globalError = true; /* ... */ }
    }

    if (userSpecificError || globalError) {
      // Revert optimistic update if any DB operation failed
      setNotifications(originalNotifications);
      // Note: We might need a more granular revert if only one part failed.
      // For simplicity, reverting all. Re-fetch of readGlobalNotificationIds will eventually correct.
    }
  };
  
  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([])
    localStorage.removeItem('newsclipping_notifications') // Also clear storage
  }
  
  // Utility function to format time ago
  const formatTimeAgo = (date: Date) => {
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) {
      return 'just now'
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
    }
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 30) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
    }
    
    const diffInMonths = Math.floor(diffInDays / 30)
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`
  }
  
  // Update times periodically
  useEffect(() => {
    const intervalId = setInterval(() => {
      setNotifications(prev => 
        prev.map(notification => ({
          ...notification,
          time: formatTimeAgo(notification.date)
        }))
      )
    }, 60000) // Update every minute
    
    return () => clearInterval(intervalId)
  }, [])
  
  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
} 